import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Friend, Role, User } from "types";
import presences from "@/presences";
import { Button } from "../ui/button";
import Icons from "@/icons";
import { useNavigate, useParams } from 'react-router-dom';
import {
    Drawer,
    DrawerContent,
    DrawerTrigger,
} from "@/components/ui/drawer"
import BotTag from "../Tags/Bot";
import permissions from "@/permissions";
import Icon from "@/icons";
import { GiveRole } from "../Dropdown/GiveRole";
import Tippy from "@tippyjs/react";
import ErrorBoundary from "@/ErrorBoundary";
import { useQueryClient } from "@tanstack/react-query";
import { FriendState } from "../Dropdown/Friend";
import toast from 'react-hot-toast';

export const UserPopover = ({ children, user, customIsOpen, setDialogOpenUser }: { children: React.ReactNode, user: User, customIsOpen?: boolean, setDialogOpenUser?: React.Dispatch<React.SetStateAction<string | null>> }) => {
    const navigate = useNavigate();
    const { teamId } = useParams();
    const createDm = async () => {
        let dm: any = await fetch(`__SITE_BASE__/api/v1/me/chats`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ users: [{ id: user.id }] }),
        });
        dm = await dm.json();
        if (dm.success) navigate(`/chat/${dm.dm.id}`);
        else toast.error(dm.message);
    }

    const removeRole = async (role: Role) => {
        if (me?.permissions
            && BigInt(String(me.permissions)) & BigInt(permissions.get("MANAGE_ROLES")!)) {
            await fetch(`__SITE_BASE__/api/v1/teams/${role.teamId}/members/${user.id}/roles/${role.id}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
            });
        }
    }

    const isPhone = window.innerWidth < 640;
    const queryClient = useQueryClient();
    const userQuery = queryClient.getQueryData(["user"]) as { user: User, friends: Friend[] };
    const me = (queryClient.getQueryData(["team", teamId, "members", "@me"] as any) as any)?.member;
    const toggleFriend = async (user: any) => {
        if (!userQuery?.friends.find((friend: Friend) => {
            return friend.users.find((u: any) => u.id === user.id);
        })) {
            const g = await fetch(`__SITE_BASE__/api/v1/me/friends`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ slug: user.subdomain }),
            });
            const data = await g.json();
            if (!data.success) toast.error(data.message);
        }
    }

    return (
        <>
            <ErrorBoundary>
                <div className="hidden sm:flex">
                    <Popover open={customIsOpen && !isPhone} onOpenChange={(isOpen) => { if (setDialogOpenUser && !isPhone) setDialogOpenUser(isOpen ? user.id : null) }} >
                        <PopoverTrigger asChild>
                            {children}
                        </PopoverTrigger>
                        <PopoverContent side="left" align="start" className="bg-brand-dark text-white border-0 p-0 rounded-lg shadow-lg ">
                            <ProfileView user={user} toggleFriend={toggleFriend} createDm={createDm} removeRole={removeRole} />
                        </PopoverContent>
                    </Popover>
                </div>

                <div className="sm:hidden flex">
                    <Drawer open={customIsOpen && isPhone} onOpenChange={(isOpen) => { if (setDialogOpenUser && isPhone) setDialogOpenUser(isOpen ? user.id : null) }}>
                        <DrawerTrigger asChild>{children}</DrawerTrigger>
                        <DrawerContent className="bg-brand-dark text-white border-0 p-0 py-0 rounded-t-lg shadow-lg focus:outline-none">
                            <ProfileView user={user} toggleFriend={toggleFriend} createDm={createDm} removeRole={removeRole} />
                        </DrawerContent>
                    </Drawer>
                </div>
            </ErrorBoundary>
        </>
    );
};

const ProfileView = ({ user, toggleFriend, createDm, removeRole }: { user: User, toggleFriend: any, createDm: any, removeRole: any }) => {
    const navigate = useNavigate();
    const { teamId } = useParams();

    const queryClient = useQueryClient();
    const userQuery = queryClient.getQueryData(["user"]) as { user: User, friends: Friend[] };
    const me = (queryClient.getQueryData(["team", teamId, "members", "@me"] as any) as any)?.member;
    const roles = (queryClient.getQueryData(["team", teamId, "roles"]) as any)?.roles as Role[];
    const team = (queryClient.getQueryData(["team", teamId]) as any)?.team


    return (
        <>
            <div className="relative border-b border-gray-600">
                <div className="bg-brand-back w-full h-24 rounded-t-lg overflow-hidden bg-gradient-to-bl from-brand-dark">
                    {user.banner &&
                        <img src={user.banner} draggable="false" className="w-full object-cover" />
                    }
                </div>
                <div className="absolute -bottom-5 left-12 border border-brand-darkest transform -translate-x-1/2 rounded-full">
                    <Avatar className="w-20 h-20 rounded-full bg-gray-600">
                        <AvatarImage src={user.profilePicture} draggable={false} />
                        <AvatarFallback className="text-2xl">{user.username[0].toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-4 border-brand-dark rounded-full" style={{ backgroundColor: presences.get(user.presence) }} />
                </div>


            </div>
            <div className="flex flex-col items-start pt-6 pb-4 px-4 text-start">
                <div className="flex items-center">
                    <p className="font-semibold text-xl">{user.username}</p>
                    <BotTag user={user} />
                </div>

                <div className="flex items-center gap-2">
                    {user.subdomain && <p className="text-gray-400 text-sm">@{user.subdomain}</p>}
                </div>

                {user.badges.length != 0 && (
                    <div className="flex items-center gap-2 mt-2 bg-brand-darkest rounded-lg">
                        <div className="grid grid-cols-10 gap-2 p-2">
                            {user.badges.map((badge: any) => (
                                <>
                                    <Tippy content={badge.name} key={badge.id} placement="top" arrow={true}>
                                        <div key={badge.id} className="flex items-center gap-2">
                                            <img src={badge.icon} alt={badge.name} className="w-4 h-4" />
                                        </div>
                                    </Tippy>
                                </>
                            ))}
                        </div>
                    </div>
                )}
            </div>


            <div className="grid gap-2 px-4 py-2 grid-cols-4 w-full items-start justify-center">
                {user.bot && (
                    <div className="flex flex-col items-center gap-2">
                        <Button className="bg-gradient-to-tr from-brand-green to-green-800/80 rounded-full w-12 h-12 hover:bg-brand-profile" onClick={() => {
                            navigate(`/b/${user.id}`)
                        }}>
                            <Icons name="plus" size="36" />
                        </Button>

                        <h3 className="text-gray-400 text-xs">Add Bot</h3>
                    </div>
                )}
                {me?.id !== user.id && (
                    <div className="flex flex-col items-center gap-2">
                        <Button className="bg-brand-back rounded-full w-12 h-12 hover:bg-brand-profile" onClick={createDm}>
                            <Icons name="message" size="36" />
                        </Button>

                        <h3 className="text-gray-400 text-xs">Message</h3>
                    </div>
                )}

                {user?.subdomain && me?.id !== user.id && (
                    <div className="flex flex-col items-center gap-2">
                        {userQuery?.friends.find((friend: Friend) => {
                            return friend.users.find((u: any) => u.id === user.id);
                        })?.status ? (
                            <FriendState friend={userQuery?.friends.find((friend: Friend) => {
                                return friend.users.find((u: any) => u.id === user.id);
                            }) as Friend}>
                                <Button className={`bg-brand-back rounded-full w-12 h-12 hover:bg-brand-profile`} onClick={() => {
                                    toggleFriend(user);
                                }}>
                                    <Icons name="applications" />
                                </Button>
                            </FriendState>
                        ) : (
                            <Button className="bg-brand-back rounded-full w-12 h-12 hover:bg-brand-profile" onClick={() => {
                                toggleFriend(user);
                            }}>
                                <Icons name="applications" />
                            </Button>
                        )}
                        <h3 className={`text-xs text-center w-full ${userQuery?.friends.find((friend: Friend) => {
                            return friend.users.find((u: any) => u.id === user.id);
                        })?.status === 'pending' ? 'text-yellow-400' : 'text-gray-400'}`}>
                            {(() => {
                                let friend = userQuery?.friends.find((friend: Friend) => {
                                    return friend.users.find((u: any) => u.id === user.id);
                                }) as Friend;
                                if (friend) {
                                    return friend.status === 'accepted' ? 'Friends' : (friend.status.charAt(0).toUpperCase() + friend.status.slice(1));
                                }
                                return 'Add Friend';
                            })()}
                        </h3>
                    </div>
                )}
            </div>
            <div className="flex items-center gap-2 px-4 py-2">
                <div className="flex flex-col">
                    <h3 className="text-gray-400 font-bold text-sm">Registered</h3>
                    <p className="text-gray-300 text-sm">{new Date(user.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
            </div>

            {!user?.roles?.includes("leave") && (user?.roles?.length != 0 || me?.permissions && Boolean(BigInt(String(me.permissions) ?? '0') & permissions.get("MANAGE_ROLES" as string)!)) && (
                <>
                    <div className="py-2">
                        <h3 className="text-gray-400 text-sm px-4 py-2 font-bold">Roles</h3>

                        <div className="flex flex-wrap items-center px-2 gap-2">
                            {me?.permissions && Boolean(BigInt(String(me.permissions) ?? '0') & permissions.get("MANAGE_ROLES" as string)!) && (
                                <div className="flex">
                                    <GiveRole user={user}>
                                        <div className="badges flex flex-wrap gap-2 bg-brand-profile rounded-full items-center justify-center" style={{ transform: "scale(0.8)" }}>
                                            <div className="flex items-start py-2 px-2">
                                                <Icon name="plus" size="16" color="#00b0ff" />
                                            </div>
                                        </div>
                                    </GiveRole>
                                </div>
                            )}

                            {roles && roles.filter(role => user?.roles?.includes(role.id) && (!team || role.id !== team.id)).sort((a, b) => b.priority - a.priority).map(role => (
                                <div key={role.id} className="flex items-center max-h-6 border rounded-lg hover:bg-brand-darkest cursor-pointer" style={{ borderColor: role.color, transform: "scale(1)" }} onClick={() => { if (!role.botScope.userId) removeRole(role) }}>
                                    <div className="flex items-center gap-2 min-w-24">
                                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: role.color }} />
                                        <p className="text-gray-300 font-semibold text-xs">{role.name.length > 10 ? role.name.slice(0, 10) + "..." : role.name}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </>

    );
}